import React from "react";
import { Box, Button, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import Trainer from "./Trainer";
import logoWithText from "../assets/images/headerlogo.svg";
import annaArt from "../assets/images/play-opponents/anna.webp";
import peterArt from "../assets/images/play-opponents/peter.webp";
import mimiArt from "../assets/images/play-opponents/mimi.webp";
import ryanArt from "../assets/images/play-opponents/ryan.webp";
import { hasFeatureAccess, isPaywallRequired, openSubscriptionGate } from "../utils/billingAccess";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";
const OPPONENTS = [
  { id: "anna", name: "Anna", info: "Passive player, chooses caution", level: 1, buyIn: 100, stakeFactor: 1, sb: 0.5, bb: 1, stakeLabel: "100NL" },
  { id: "peter", name: "Peter", info: "Weak regular, too passive", level: 2, buyIn: 500, stakeFactor: 5, sb: 2.5, bb: 5, stakeLabel: "500NL" },
  { id: "mimi", name: "Mimi", info: "Loose-aggressive player, overbluffs", level: 3, buyIn: 2000, stakeFactor: 20, sb: 10, bb: 20, stakeLabel: "2000NL" },
  { id: "ryan", name: "Ryan", info: "Strong pro, tough spots", level: 4, buyIn: 5000, stakeFactor: 50, sb: 25, bb: 50, stakeLabel: "5000NL" },
];
const OPPONENT_ART = {
  anna: annaArt,
  peter: peterArt,
  mimi: mimiArt,
  ryan: ryanArt,
};
const COLORS = {
  bg: "#101012",
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#8f8f8f",
  white: "#ffffff",
  gold: "#B99E4F",
};

function defaultProfile() {
  return {
    unlockedLevel: 1,
    overallScore: 0,
    sessions: [],
    vsTotals: {},
  };
}

export default function Play() {
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState(() => defaultProfile());
  const [bankroll, setBankroll] = React.useState(0);
  const [bankrollMax, setBankrollMax] = React.useState(500);
  const [bankrollNextRegenInMs, setBankrollNextRegenInMs] = React.useState(0);
  const [regenTargetTs, setRegenTargetTs] = React.useState(0);
  const [clockNow, setClockNow] = React.useState(() => Date.now());
  const [loadingProfile, setLoadingProfile] = React.useState(true);
  const [lobbyError, setLobbyError] = React.useState("");
  const [activePlay, setActivePlay] = React.useState(null); // { opponentId, mode, token }
  const [activeSession, setActiveSession] = React.useState(null); // server-side seated session { opponentId, buyIn, ... }
  const getBillingState = React.useCallback(() => {
    if (typeof window === "undefined") return null;
    return window.__pmBillingState || null;
  }, []);
  const playMaxOpponentLevel = React.useCallback(() => {
    const billing = getBillingState();
    if (!billing?.authed) return 0;
    if (billing?.hasAccess && typeof billing.hasAccess === "object") {
      if (Object.prototype.hasOwnProperty.call(billing.hasAccess, "playMaxOpponentLevel")) {
        return Math.max(0, Number(billing.hasAccess.playMaxOpponentLevel || 0));
      }
      // Legacy: any play access unlocked all opponents.
      return billing.hasAccess.play ? 4 : 0;
    }
    return billing?.hasPaidAccess ? 4 : 0;
  }, [getBillingState]);
  const canPlayOpponent = React.useCallback((opponent) => {
    const level = Number(opponent?.level || 0);
    return level > 0 && level <= playMaxOpponentLevel();
  }, [playMaxOpponentLevel]);
  const requireOpponentAccess = React.useCallback((opponent, stage = "play_premium") => {
    if (canPlayOpponent(opponent)) return true;
    try {
      if (typeof window.__pmOpenSubscriptionGate === "function") {
        window.__pmOpenSubscriptionGate();
      } else {
        window.dispatchEvent(new CustomEvent("pm:require-subscription", { detail: { stage } }));
      }
    } catch (_) {}
    return false;
  }, [canPlayOpponent]);

  const loadRemoteProfile = React.useCallback(async () => {
    setLoadingProfile(true);
    try {
      const res = await fetch(`${SOLVER_API_URL}/api/auth/play/profile`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setLobbyError(data?.error || "Could not load play profile.");
        return;
      }
      setProfile(data?.profile || defaultProfile());
      setBankroll(Number(data?.bankroll || 0));
      setBankrollMax(Number(data?.bankrollMax || 500));
      setActiveSession(data?.activeSession?.opponentId ? data.activeSession : null);
      const nextMs = Number(data?.bankrollNextRegenInMs || 0);
      setBankrollNextRegenInMs(nextMs);
      setRegenTargetTs(nextMs > 0 ? Date.now() + nextMs : 0);
    } catch (_) {
      setLobbyError("Could not contact server.");
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  React.useEffect(() => {
    loadRemoteProfile();
  }, [loadRemoteProfile]);

  // If trial expires mid-session, kick back to lobby and open paywall.
  React.useEffect(() => {
    if (!activePlay) return undefined;
    const stopIfLocked = () => {
      if (isPaywallRequired() || !hasFeatureAccess("play") || playMaxOpponentLevel() <= 0) {
        setActivePlay(null);
        openSubscriptionGate("play");
      }
    };
    stopIfLocked();
    window.addEventListener("pm:billing-refresh", stopIfLocked);
    return () => window.removeEventListener("pm:billing-refresh", stopIfLocked);
  }, [activePlay, playMaxOpponentLevel]);

  React.useEffect(() => {
    const id = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    if (!loadingProfile && bankroll < bankrollMax && regenTargetTs > 0 && clockNow >= regenTargetTs) {
      loadRemoteProfile();
    }
  }, [bankroll, bankrollMax, clockNow, loadRemoteProfile, loadingProfile, regenTargetTs]);

  const beginPlay = async (opponentId, { resumeOnly = false } = {}) => {
    setLobbyError("");
    if (isPaywallRequired() || !hasFeatureAccess("play") || playMaxOpponentLevel() <= 0) {
      openSubscriptionGate("play");
      return;
    }
    const id = String(opponentId || "").toLowerCase();
    const opponent = OPPONENTS.find((o) => o.id === id);
    const seatedOpponentId = String(activeSession?.opponentId || "").toLowerCase();
    const isResumingSeated = seatedOpponentId === id;
    // Already seated at this table: allow Continue without a fresh plan/buy-in check.
    if (!isResumingSeated && (!opponent || !requireOpponentAccess(opponent, `play_${id}`))) {
      return;
    }
    if (resumeOnly && !isResumingSeated) {
      setLobbyError("No seated session found for this opponent.");
      return;
    }
    const startSession = async () => {
      const res = await fetch(`${SOLVER_API_URL}/api/auth/play/session/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ opponentId: id }),
      });
      const data = await res.json().catch(() => ({}));
      return { res, data };
    };
    try {
      let { res, data } = await startSession();
      if (res.status === 409 && data?.activeSession?.opponentId) {
        const activeId = String(data.activeSession.opponentId || "").toLowerCase();
        // Same opponent: resume that active session (chips already on the table).
        if (activeId === id) {
          const resumedOpponent = OPPONENTS.find((o) => o.id === activeId) || opponent || OPPONENTS[0];
          setActiveSession(data.activeSession);
          setActivePlay({
            opponentId: resumedOpponent.id,
            mode: String(data?.activeSession?.mode || "play"),
            token: `${Date.now()}-${resumedOpponent.id}-resume`,
            opponent: resumedOpponent,
          });
          setLobbyError("");
          return;
        }
        if (resumeOnly) {
          setLobbyError("You are seated at a different table. Continue there first.");
          return;
        }
        // Different opponent chosen: close the old session, then start the requested one.
        await fetch(`${SOLVER_API_URL}/api/auth/play/session/finalize`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            opponentId: activeId,
            mode: String(data?.activeSession?.mode || "play"),
            hands: 0,
            score: 0,
          }),
        }).catch(() => ({}));
        ({ res, data } = await startSession());
      }
      if (!res.ok || !data?.success) {
        setLobbyError(data?.error || "Could not start session.");
        await loadRemoteProfile();
        return;
      }
      const startedOpponent = OPPONENTS.find((o) => o.id === id) || OPPONENTS[0];
      setBankroll(Number(data?.bankroll || 0));
      setActiveSession(data?.activeSession || { opponentId: id, buyIn: startedOpponent.buyIn, mode: "play" });
      setActivePlay({
        opponentId: id,
        mode: "play",
        token: `${Date.now()}-${id}-play`,
        opponent: startedOpponent,
      });
    } catch (_) {
      setLobbyError("Could not contact server.");
    }
  };

  const handleFinalizeSession = React.useCallback(async (payload) => {
    if (!payload) return;
    try {
      const res = await fetch(`${SOLVER_API_URL}/api/auth/play/session/finalize`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        setProfile(data?.profile || defaultProfile());
        setBankroll(Number(data?.bankroll || 0));
        setActiveSession(null);
        if (payload?.restartSession && activePlay?.opponentId) {
          const restartRes = await fetch(`${SOLVER_API_URL}/api/auth/play/session/start`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ opponentId: activePlay.opponentId, mode: activePlay.mode || "casual" }),
          });
          const restartData = await restartRes.json().catch(() => ({}));
          if (restartRes.ok && restartData?.success) {
            setBankroll(Number(restartData?.bankroll || 0));
            setActiveSession(
              restartData?.activeSession || {
                opponentId: activePlay.opponentId,
                buyIn: Number(activePlay?.opponent?.buyIn || 0),
                mode: activePlay.mode || "play",
              }
            );
          } else {
            setLobbyError(restartData?.error || "Could not start next session buy-in.");
            await loadRemoteProfile();
          }
        }
        return;
      }
      setLobbyError(data?.error || "Could not finalize session.");
    } catch (_) {
      setLobbyError("Could not contact server.");
    }
  }, [activePlay, loadRemoteProfile]);

  const formatChipCount = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v)) return "0";
    return Math.round(v).toLocaleString();
  };

  const formatTimeLeft = (ms) => {
    const total = Math.max(0, Math.floor(Number(ms || 0) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const liveRegenMs = bankroll < bankrollMax
    ? Math.max(0, regenTargetTs > 0 ? regenTargetTs - clockNow : bankrollNextRegenInMs)
    : 0;
  const navTabSx = (active = false) => ({
    minWidth: "unset",
    px: { xs: 1.6, sm: 2.1 },
    py: { xs: 0.45, sm: 0.6 },
    borderRadius: "999px",
    border: "none",
    backgroundColor: active ? "rgba(185,158,79,0.14)" : "rgba(255,255,255,0.02)",
    color: active ? COLORS.white : COLORS.textMuted,
    fontSize: { xs: "11px", sm: "12px" },
    fontWeight: active ? 700 : 600,
    textTransform: "none",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    transition: "all 160ms ease",
    "&:hover": {
      color: COLORS.white,
      backgroundColor: active ? "rgba(185,158,79,0.22)" : "rgba(255,255,255,0.07)",
    },
  });

  if (activePlay) {
    const opponent = activePlay?.opponent || OPPONENTS.find((o) => o.id === activePlay.opponentId) || OPPONENTS[0];
    return (
      <Trainer
        key={activePlay.token}
        modeVariant="play"
        playConfig={{
          opponentId: opponent.id,
          opponentName: opponent.name,
          mode: activePlay.mode,
          opponentImageUrl: OPPONENT_ART[opponent.id] || "",
          buyIn: Number(opponent.buyIn || 100),
          stakeFactor: Number(opponent.stakeFactor || 1),
          sb: Number(opponent.sb || 0.5),
          bb: Number(opponent.bb || 1),
          stakeLabel: String(opponent.stakeLabel || "100NL"),
        }}
        onPlaySessionFinalize={handleFinalizeSession}
        onExitPlayLobby={async () => {
          await loadRemoteProfile();
          setActivePlay(null);
        }}
      />
    );
  }

  const rowsTopToBottom = [...OPPONENTS].reverse(); // Ryan top -> Anna bottom
  const lastThree = (profile.sessions || []).slice(0, 3);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: COLORS.bg, color: COLORS.text }}>
      <Box sx={{ py: 1.5, px: 2, pl: { xs: 1.2, sm: 3 }, pr: { xs: 14, sm: 2 }, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.6, sm: 1.2 }, minWidth: 0, flex: 1, overflowX: "auto" }}>
          <img src={logoWithText} alt="PokerMoose" onClick={() => navigate("/")} style={{ height: 32, width: 32, flexShrink: 0, opacity: 0.5, filter: "grayscale(1)", cursor: "pointer" }} />
          <Typography
            sx={{
              display: { xs: "none", sm: "inline" },
              color: COLORS.white,
              fontSize: "13px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontFamily: "Play, Arial, sans-serif",
              lineHeight: 1,
              mr: 0.4,
              userSelect: "none",
              flexShrink: 0,
            }}
          >
            PokerMoose
          </Typography>
          <Button variant="text" size="small" onClick={() => navigate("/")} sx={{ ...navTabSx(false), flexShrink: 0 }}>Home</Button>
          <Button variant="text" size="small" onClick={() => navigate("/cash")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Solve</Button>
          <Button variant="text" size="small" onClick={() => navigate("/trainer")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Train</Button>
          <Button variant="text" size="small" onClick={() => navigate("/play")} sx={{ ...navTabSx(true), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Play</Button>
          <Button variant="text" size="small" onClick={() => navigate("/analysis")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Analyze</Button>
        </Box>
        <Box />
      </Box>
      <Box
        sx={{
          minHeight: "calc(100vh - 66px)",
          px: { xs: 1.2, sm: 2 },
          py: { xs: 1.2, sm: 2 },
          display: "flex",
          alignItems: "center",
        }}
      >
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", display: "flex", flexDirection: "column", gap: 1.3, alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ width: "100%", border: "1px solid #2a2a2a", borderRadius: "12px", p: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "#999", mb: 0.25 }}>Lobby Bankroll</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#56ECCF" }}>
            {formatChipCount(bankroll)} chips
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#8f8f8f", mt: 0.25 }}>
            +100 chips every 24h (max {formatChipCount(bankrollMax)})
            {bankroll < bankrollMax && liveRegenMs > 0 ? ` • next in ${formatTimeLeft(liveRegenMs)}` : ""}
          </Typography>
          {!!activeSession?.opponentId && (() => {
            const seated = OPPONENTS.find((o) => o.id === String(activeSession.opponentId || "").toLowerCase());
            const seatedBuyIn = Number(activeSession.buyIn || seated?.buyIn || 0);
            return (
              <Typography sx={{ fontSize: 11, color: COLORS.white, mt: 0.55, fontWeight: 700 }}>
                {formatChipCount(seatedBuyIn)} chips seated with {seated?.name || activeSession.opponentId}. Press continue to keep playing
              </Typography>
            );
          })()}
        </Box>
        <Box sx={{ width: "100%", border: "1px solid #2a2a2a", borderRadius: "12px", p: 1 }}>
          {rowsTopToBottom.map((op) => {
            const seatedOpponentId = String(activeSession?.opponentId || "").toLowerCase();
            const isContinue = seatedOpponentId === op.id;
            const affordable = Number(bankroll || 0) >= Number(op.buyIn || 0);
            const vsScore = Number(profile.vsTotals?.[op.id] || 0);
            const isPaywalled = !isContinue && !canPlayOpponent(op);
            return (
              <Box key={op.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "110px 1fr 118px 116px 220px" }, gap: 0.9, alignItems: "center", borderBottom: "1px solid #222", py: 0.8 }}>
                <Box sx={{ width: 98, height: 75, border: "1px dashed #444", borderRadius: "6px", backgroundColor: "#101010", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img
                    src={OPPONENT_ART[op.id] || ""}
                    alt={op.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: op.id === "peter" ? "scale(1.12)" : "scale(1)" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{op.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: "#999" }}>{op.info}</Typography>
                  <Typography sx={{ fontSize: 10, color: "#b9b9b9", mt: 0.15 }}>
                    Buy-in {formatChipCount(op.buyIn)} • {op.stakeLabel} ({op.sb} / {op.bb})
                    {isContinue ? " • Seated" : ""}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: "#bbb", textAlign: "center" }}>Level {op.level}</Typography>
                <Typography sx={{ fontSize: 12, color: vsScore >= 0 ? "#56ECCF" : "#e57f7f", textAlign: "right" }}>
                  {vsScore >= 0 ? "+" : ""}{formatChipCount(vsScore)}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.6, justifyContent: "flex-end" }}>
                  {isContinue ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={loadingProfile}
                      onClick={() => beginPlay(op.id, { resumeOnly: true })}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        minWidth: 130,
                        backgroundColor: "#56ECCF",
                        color: "#111",
                        fontWeight: 800,
                        "&:hover": { backgroundColor: "rgb(45, 179, 155)" },
                      }}
                    >
                      Continue
                    </Button>
                  ) : isPaywalled ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => requireOpponentAccess(op, `play_${op.id}`)}
                      startIcon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        minWidth: 130,
                        backgroundColor: "#4a4a4a",
                        color: "#d7d7d7",
                        "&:hover": { backgroundColor: "#595959" },
                      }}
                    >
                      Play
                    </Button>
                  ) : affordable ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={loadingProfile}
                      onClick={() => beginPlay(op.id)}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        minWidth: 130,
                        backgroundColor: "#B99E4F",
                        color: "#111",
                        "&:hover": { backgroundColor: "#a88f47" },
                        "&.Mui-disabled": { backgroundColor: "#6e6135", color: "#1f1f1f", opacity: 1 },
                      }}
                    >
                      Play
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      disabled
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        minWidth: 130,
                        backgroundColor: "#4a4a4a",
                        color: "#c8c8c8",
                        opacity: 1,
                        "&.Mui-disabled": { backgroundColor: "#4a4a4a", color: "#c8c8c8", opacity: 1 },
                      }}
                    >
                      Not enough chips
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
          {!!lobbyError && (
            <Typography sx={{ fontSize: 11, color: "#e57f7f", mt: 0.65, textAlign: "center" }}>
              {lobbyError}
            </Typography>
          )}
        </Box>

        <Box sx={{ width: "100%", border: "1px solid #2a2a2a", borderRadius: "12px", p: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "#999", mb: 0.4 }}>Overall chip score</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: Number(profile.overallScore || 0) >= 0 ? "#56ECCF" : "#e57f7f", mb: 1 }}>
            {Number(profile.overallScore || 0) >= 0 ? "+" : ""}{formatChipCount(profile.overallScore)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#bbb", mb: 0.6 }}>Last 3 sessions</Typography>
          {!lastThree.length && <Typography sx={{ fontSize: 11, color: "#777" }}>No sessions yet.</Typography>}
          {lastThree.map((s) => (
            <Typography key={s.id} sx={{ fontSize: 11, color: Number(s.score || 0) >= 0 ? "#56ECCF" : "#e57f7f", mb: 0.25 }}>
              {s.opponentName} ({s.mode}) {Number(s.score || 0) >= 0 ? "+" : ""}{formatChipCount(s.score)} ({Number(s.hands || 0)}h)
            </Typography>
          ))}
        </Box>
      </Box>
      </Box>
    </Box>
  );
}

